import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Sparkles, ListChecks, ArrowLeft, Trash2, Lock, CheckCircle2, AlertCircle, ShieldAlert, Download,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { RiskBadge } from "@/components/risk/RiskBadge";
import { RiskGauge } from "@/components/risk/RiskGauge";
import { SubscoreBars } from "@/components/risk/SubscoreBars";
import { LegalDisclaimer } from "@/components/common/LegalDisclaimer";
import { useAuth } from "@/context/AuthContext";
import { EVIDENCE_CATEGORIES, subColor } from "@/lib/risk";
import api, { getErrorMessage, downloadReportPdf } from "@/lib/api";

const HERO_ANIM = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } };

export default function Result() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const isPaid = user?.plan === "pro" || user?.plan === "pm";

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadReportPdf(id, `hostguard-evidence-${(a?.guest_initials || "report").replace(/[^a-zA-Z0-9]/g, "")}.pdf`);
      toast.success("Evidence report downloaded");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not generate the PDF."));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    api.get(`/assessments/${id}`).then((r) => setA(r.data)).catch(() => toast.error("Assessment not found")).finally(() => setLoading(false));
  }, [id]);

  // Memoized so the split/filter/map doesn't recompute on every render
  const aiParagraphs = useMemo(
    () => (a?.ai_summary ? a.ai_summary.split("\n").filter(Boolean) : []),
    [a?.ai_summary]
  );

  const remove = async () => {
    try {
      await api.delete(`/assessments/${id}`);
      toast.success("Assessment deleted");
      navigate("/app/history");
    } catch {
      toast.error("Could not delete");
    }
  };

  if (loading) {
    return <AppShell title="Risk Result"><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppShell>;
  }
  if (!a) {
    return <AppShell title="Risk Result"><p className="text-muted-foreground">Assessment not found.</p></AppShell>;
  }

  return (
    <AppShell title="Risk Result" action={
      <div className="flex items-center gap-2">
        {isPaid ? (
          <Button size="sm" className="gap-2" disabled={downloading} onClick={downloadPdf} data-testid="result-download-pdf">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </Button>
        ) : (
          <Link to="/app/pricing"><Button size="sm" variant="outline" className="gap-2" data-testid="result-download-locked"><Lock className="h-4 w-4" /> PDF (Pro)</Button></Link>
        )}
        <Button variant="outline" size="sm" onClick={remove} className="gap-2 text-muted-foreground" data-testid="result-delete-button"><Trash2 className="h-4 w-4" /> Delete</Button>
      </div>
    }>
      <div className="space-y-6">
        <Link to="/app/history" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to history</Link>

        {/* Prominent informational disclaimer (required on every result) */}
        <div
          className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4"
          data-testid="result-disclaimer-banner"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className="text-sm leading-relaxed text-foreground">
            HostGuard provides informational risk guidance only and does not guarantee booking outcomes.
            Users remain solely responsible for booking decisions.{" "}
            <Link to="/disclaimer" className="font-medium text-primary hover:underline" data-testid="result-disclaimer-link">
              Read the full disclaimer
            </Link>.
          </p>
        </div>

        {/* Hero score */}
        <motion.div {...HERO_ANIM} className="grid gap-6 rounded-2xl border bg-card p-6 lg:grid-cols-2 lg:p-8">
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3">
              <RiskBadge band={a.risk_band} data-testid="risk-result-band" />
              <span className="text-sm text-muted-foreground">{a.guest_initials || "—"} · {a.platform}</span>
            </div>
            <h2 className="mt-4 font-display text-2xl font-semibold">Overall booking risk: {a.risk_band}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              This score is calculated from the booking characteristics you provided. Bands: 0–30 Low, 31–65 Medium, 66–100 High.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">{a.property_name ? `Property: ${a.property_name} · ` : ""}{new Date(a.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center justify-center">
            <RiskGauge score={a.overall_score} band={a.risk_band} size={280} />
          </div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Sub-scores */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="mb-1 font-display text-base font-semibold">Risk breakdown</h3>
            <p className="mb-5 text-xs text-muted-foreground">Themed sub-scores derived from booking details.</p>
            <SubscoreBars subscores={a.subscores} />
          </div>

          {/* Triggered factors */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="mb-1 font-display text-base font-semibold">Contributing factors</h3>
            <p className="mb-4 text-xs text-muted-foreground">Booking characteristics that influenced the score.</p>
            {a.triggered_factors?.length ? (
              <ul className="space-y-2" data-testid="result-factors-list">
                {a.triggered_factors.map((t) => (
                  <li key={t.label} className="flex items-center justify-between rounded-lg border bg-secondary/30 px-3 py-2 text-sm">
                    <span>{t.label}</span>
                    <span className={`tabular-nums font-medium ${t.points > 0 ? "text-[hsl(var(--risk-high))]" : "text-[hsl(var(--risk-low))]"}`}>{t.points > 0 ? `+${t.points}` : t.points}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-muted-foreground">No notable risk factors identified.</p>}
          </div>
        </div>

        {/* Evidence risk categories (derived) */}
        {a.categories && (
          <div className="rounded-xl border bg-card p-6" data-testid="result-evidence-categories">
            <h3 className="mb-1 font-display text-base font-semibold">Evidence risk categories</h3>
            <p className="mb-4 text-xs text-muted-foreground">Dispute-relevant categories derived from the same booking signals — useful as supporting evidence.</p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {EVIDENCE_CATEGORIES.map(({ key, short }) => {
                const v = a.categories[key] ?? 0;
                return (
                  <div key={key} data-testid={`result-cat-${key}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{short}</span>
                      <span className="font-medium tabular-nums" style={{ color: subColor(v) }}>{v}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted">
                      <div className="h-full rounded-full" style={{ width: `${v}%`, backgroundColor: subColor(v) }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold">AI risk summary</h3>
          </div>
          {a.ai_enabled && a.ai_summary ? (
            <div className="space-y-3 text-sm leading-relaxed text-foreground/90" data-testid="result-ai-summary">
              {aiParagraphs.map((para, i) => <p key={`para-${i}-${para.slice(0, 16)}`}>{para}</p>)}
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed bg-secondary/30 p-5" data-testid="ai-summary-locked">
              <span className="inline-flex items-center gap-2 text-sm font-medium"><Lock className="h-4 w-4 text-muted-foreground" /> AI summaries are a Pro feature</span>
              <p className="text-sm text-muted-foreground">Upgrade to Pro Host to get a cautious, plain-English risk narrative for every booking.</p>
              <Link to="/app/pricing"><Button size="sm" data-testid="result-upgrade-button">Upgrade to Pro</Button></Link>
            </div>
          )}
        </div>

        {/* Recommendations */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold">Recommended precautions</h3>
          </div>
          <ul className="space-y-2.5" data-testid="result-recommendations-list">
            {a.recommendations.map((r) => (
              <li key={r} className="flex items-start gap-2.5 text-sm">
                {a.risk_band === "High" ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--risk-high))]" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--risk-low))]" />}
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        <LegalDisclaimer />
      </div>
    </AppShell>
  );
}
